import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Inbox, Loader2, Pencil, RotateCcw, Send, Trash2, Link2 } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api';
import { DataTable, type Column } from '../components/DataTable';
import { EmptyState } from '../components/EmptyState';
import { Filters, useFilters, type FilterField } from '../components/Filters';
import { Modal } from '../components/Modal';
import { SelectionBar, type BulkAction } from '../components/SelectionBar';
import { useSession } from '../lib/session';
import type { ForwardedMessage, PowerMeter } from '../lib/types';

/**
 * Everything the paying handset has forwarded, and what became of it.
 *
 * ## Why this screen exists
 *
 * The automatic path matches a forwarded message to a purchase and sends the
 * token on by itself, and when it works nobody needs to look here. It does not
 * always work: the first genuine KPLC token this system ever received named a
 * meter that had never been registered, so nothing could match it, and the
 * twenty digits a landlord was waiting for were thrown away.
 *
 * Now every message is kept, and this is where somebody finds the one that did
 * not land — files it under the right landlord, corrects anything a parser
 * misread, and sends it with a button.
 *
 * ## Why the token is on the screen
 *
 * Because there is no point in any of the above otherwise. The page is behind
 * `billing:read` and the row exists precisely to hold the digits until
 * somebody passes them on; a list that hid them would leave the recovery path
 * with nothing to copy. Sending is a separate permission and a separate press.
 */
export function ForwardedInbox({ meters }: { meters: PowerMeter[] }) {
  const queryClient = useQueryClient();
  const { can } = useSession();

  const [state, setState] = useState<'all' | 'unmatched' | 'unsent' | 'sent' | 'trash'>('all');
  const [assigning, setAssigning] = useState<ForwardedMessage | null>(null);
  const [editing, setEditing] = useState<ForwardedMessage | null>(null);
  const [reading, setReading] = useState<ForwardedMessage | null>(null);
  const [sending, setSending] = useState<ForwardedMessage | null>(null);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const mayEdit = can('subscription:write');
  const maySend = can('payment:record');
  /*
   * A stronger gate than the trash beside it. Moving something out of the way
   * is reversible and about a day's work; destroying it is neither, and the
   * reason to reach for it is usually that the message should never have been
   * collected at all.
   */
  const mayDelete = can('settings:write');

  const messages = useQuery({
    queryKey: ['forwarded-messages', state],
    queryFn: () => api.forwardedMessages({ state }),
    /*
     * Polled for the same reason the in-flight panel is: what this list shows
     * arrives from outside the browser. A handset posts a message and nothing
     * here would ever hear about it. Slower than the progress panel, because
     * this is a list somebody reads rather than a wait somebody watches.
     */
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
  });

  const refresh = async () => {
    setError(null);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['forwarded-messages'] }),
      queryClient.invalidateQueries({ queryKey: ['power-outstanding'] }),
      queryClient.invalidateQueries({ queryKey: ['power-readiness'] }),
    ]);
  };

  const all = messages.data ?? [];

  const FIELDS: FilterField<ForwardedMessage>[] = [
    { key: 'meter', label: 'Meter number', type: 'text', value: (m) => m.meterNumber },
    { key: 'landlord', label: 'Landlord', type: 'text', value: (m) => m.landlordName },
    { key: 'receipt', label: 'M-Pesa receipt', type: 'text', value: (m) => m.receipt },
    { key: 'sender', label: 'Sender', type: 'text', value: (m) => m.sender },
    { key: 'body', label: 'Message text', type: 'text', value: (m) => m.body },
    {
      key: 'kind',
      label: 'Kind',
      type: 'choice',
      options: [
        { value: 'token', label: 'Token' },
        { value: 'payment', label: 'Payment' },
        { value: 'unknown', label: 'Neither' },
      ],
      value: (m) => m.kind,
    },
    {
      key: 'state',
      label: 'State',
      type: 'choice',
      options: [
        { value: 'unmatched', label: 'Needs a landlord' },
        { value: 'ready', label: 'Ready to send' },
        { value: 'sent', label: 'Sent' },
        { value: 'trash', label: 'In the trash' },
      ],
      value: (m) => m.state,
    },
    { key: 'received', label: 'Arrived', type: 'date', value: (m) => m.receivedAt },
    { key: 'amount', label: 'Amount', type: 'number', value: (m) => (m.amountCents ?? 0) / 100 },
  ];

  const { query, setQuery, clauses, setClauses, filtered } = useFilters(all, FIELDS);

  const actions: BulkAction<ForwardedMessage>[] = [
    { key: 'print', label: 'Print', prominent: true, run: () => window.print() },
    ...(mayEdit
      ? [
          {
            key: 'trash',
            label: 'Move to trash',
            confirm: (picked: ForwardedMessage[]) =>
              `Put ${picked.length} message${picked.length === 1 ? '' : 's'} away? ` +
              'Nothing is deleted — they stay in the trash and can be brought back.',
            run: async (picked: ForwardedMessage[]) => {
              for (const one of picked) await api.removeForwardedMessage(one.id);
              setChosen(new Set());
              await refresh();
            },
          },
          {
            key: 'restore',
            label: 'Restore',
            run: async (picked: ForwardedMessage[]) => {
              for (const one of picked) await api.restoreForwardedMessage(one.id);
              setChosen(new Set());
              await refresh();
            },
          },
        ]
      : []),
  ];

  const columns: Column<ForwardedMessage>[] = [
    {
      key: 'arrived',
      header: 'Arrived',
      sortBy: (m) => m.receivedAt,
      cell: (m) => (
        <span>
          <span className="block">{new Date(m.receivedAt).toLocaleString('en-KE')}</span>
          <span className="block text-xs text-muted-foreground">
            {m.sender ? `from ${m.sender}` : 'sender not stated'}
          </span>
        </span>
      ),
    },
    {
      key: 'what',
      header: 'What it says',
      sortBy: (m) => m.kind,
      cell: (m) => (
        <span>
          {/*
            * The last four digits, not the twenty.
            *
            * This list sits on the Power and tokens page, which is left open
            * in an office and refetches on its own — and a token is a bearer
            * credential for somebody else's electricity. Four digits are
            * enough to tell two rows apart, which is all this column is for.
            *
            * The whole token is still one press away, in "Read it" and on the
            * landlord's own details, where somebody has deliberately gone to
            * look at it.
            */}
          <span className="font-mono text-sm">{m.token ? `···· ${m.token.slice(-4)}` : '—'}</span>
          <span className="block text-xs text-muted-foreground">
            {[
              m.meterNumber ? `Meter ${m.meterNumber}` : null,
              m.units === null ? null : `${m.units} units`,
              m.amount,
              m.receipt,
            ]
              .filter(Boolean)
              .join(' · ') || 'nothing could be read out of it'}
          </span>
        </span>
      ),
    },
    {
      key: 'landlord',
      header: 'Landlord',
      sortBy: (m) => m.landlordName ?? null,
      cell: (m) =>
        m.landlordName ? (
          <span>
            {m.landlordName}
            <span className="block text-xs text-muted-foreground">{m.meterLabel}</span>
          </span>
        ) : (
          <span className="text-muted-foreground">
            —
            <span className="block text-xs">{m.unmatchedReason ?? 'not filed yet'}</span>
          </span>
        ),
    },
    {
      key: 'state',
      header: 'State',
      sortBy: (m) => m.state,
      cell: (m) => <StateChip message={m} />,
    },
    {
      key: 'do',
      header: '',
      cell: (m) => (
        <div className="flex flex-wrap justify-end gap-1">
          <button type="button" className="btn-quiet px-2 py-1 text-xs" onClick={() => setReading(m)}>
            Read it
          </button>

          {mayEdit && !m.sentAt && !m.deletedAt && (
            <>
              <button
                type="button"
                className="btn-quiet px-2 py-1 text-xs"
                onClick={() => setAssigning(m)}
                title="Say which landlord this belongs to"
              >
                <Link2 className="h-3.5 w-3.5" aria-hidden="true" />
                Landlord
              </button>
              <button
                type="button"
                className="btn-quiet px-2 py-1 text-xs"
                onClick={() => setEditing(m)}
                title="Correct what was read out of it"
              >
                <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                Correct
              </button>
            </>
          )}

          {/*
            * Shown whenever there is something to send, disabled when there is
            * a reason not to, and the reason is the tooltip. A button that
            * vanishes leaves somebody wondering where it went; one that says
            * "choose which landlord it belongs to first" tells them what to
            * do next.
            */}
          {maySend && !m.deletedAt && !m.sentAt && (
            <button
              type="button"
              className="btn-primary px-2 py-1 text-xs"
              disabled={!m.canSend}
              title={m.blockedBecause ?? 'Send this token to the landlord'}
              onClick={() => setSending(m)}
            >
              <Send className="h-3.5 w-3.5" aria-hidden="true" />
              Send
            </button>
          )}

          {mayEdit &&
            (m.deletedAt ? (
              <button
                type="button"
                className="btn-quiet px-2 py-1 text-xs"
                onClick={async () => {
                  await api.restoreForwardedMessage(m.id);
                  await refresh();
                }}
              >
                <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                Restore
              </button>
            ) : (
              <button
                type="button"
                className="btn-quiet px-2 py-1 text-xs"
                onClick={async () => {
                  await api.removeForwardedMessage(m.id);
                  await refresh();
                }}
                title="Put it away. Nothing is deleted."
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            ))}

          {/*
            Deleting for good, which the trash deliberately does not do.
            Asked for before it happens, and the question names what goes with
            it — an unsent token is kept nowhere else.
          */}
          {mayDelete && (
            <button
              type="button"
              className="btn-quiet px-2 py-1 text-xs text-destructive"
              onClick={async () => {
                const losesToken = Boolean(m.token) && !m.sentAt;
                const warning = losesToken
                  ? `This message carries a token that has not been sent to anyone. Deleting it loses the token — it is kept nowhere else.

Delete it anyway?`
                  : 'Delete this message for good? There is no trash and nothing to undo it with.';
                if (!window.confirm(warning)) return;
                await api.purgeForwardedMessage(m.id);
                await refresh();
              }}
              title="Delete for good. This cannot be undone."
            >
              Delete
            </button>
          )}
        </div>
      ),
    },
  ];

  const waiting = all.filter((one) => one.state === 'unmatched').length;

  return (
    <section className="card" data-forwarded-inbox>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-lg">Messages from the paying phone</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Every message the handset has forwarded, kept whether or not it matched a purchase. A
            token that names a meter nobody has registered lands here instead of being lost &mdash;
            say whose it is, then send it.
          </p>
        </div>

        {/*
          * The three questions people actually ask of this list, as buttons.
          * They are not the same shape as any one column, which is why they are
          * here and not in the filter panel below.
          */}
        <div className="flex flex-wrap gap-1" role="group" aria-label="Which messages">
          {(
            [
              ['all', 'All'],
              ['unmatched', `Needs a landlord${waiting ? ` (${waiting})` : ''}`],
              ['unsent', 'Not sent'],
              ['sent', 'Sent'],
              ['trash', 'Trash'],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={state === key ? 'btn-primary px-2 py-1 text-xs' : 'btn-quiet px-2 py-1 text-xs'}
              onClick={() => {
                setState(key);
                setChosen(new Set());
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <p className="mt-3 rounded-xl border border-danger-300 bg-danger-50 p-3 text-sm text-danger-700">
          {error}
        </p>
      )}

      {messages.isLoading ? (
        <p className="py-6 text-center text-sm text-muted-foreground">Looking&hellip;</p>
      ) : all.length === 0 ? (
        <EmptyState
          icon={Inbox}
          headline="Nothing has been forwarded yet"
          body="When the paying handset forwards an M-Pesa confirmation or a KPLC token, it appears here — matched or not."
        />
      ) : (
        <>
          <Filters<ForwardedMessage>
            fields={FIELDS}
            query={query}
            onQuery={setQuery}
            clauses={clauses}
            onClauses={setClauses}
            total={all.length}
            showing={filtered.length}
            placeholder="Search by meter, landlord, receipt or the text itself"
          />

          <SelectionBar<ForwardedMessage>
            rows={filtered}
            selected={chosen}
            onClear={() => setChosen(new Set())}
            rowKey={(m) => m.id}
            exportName="forwarded-messages"
            csv={[
              { header: 'Arrived', value: (m) => new Date(m.receivedAt).toISOString() },
              { header: 'Sender', value: (m) => m.sender ?? '' },
              { header: 'Kind', value: (m) => m.kind },
              { header: 'Meter', value: (m) => m.meterNumber ?? '' },
              { header: 'Landlord', value: (m) => m.landlordName ?? '' },
              { header: 'Units', value: (m) => (m.units === null ? '' : String(m.units)) },
              { header: 'Amount', value: (m) => m.amount ?? '' },
              { header: 'Receipt', value: (m) => m.receipt ?? '' },
              { header: 'State', value: (m) => m.state },
              { header: 'Sent at', value: (m) => (m.sentAt ? new Date(m.sentAt).toISOString() : '') },
              { header: 'Sent to', value: (m) => m.sentTo ?? '' },
              /*
               * The token is deliberately absent from the export. A CSV leaves
               * this system and lands in a downloads folder, an email, a chat —
               * and it is a bearer credential for somebody else's electricity.
               * On the screen it is behind a login; in a spreadsheet it is not.
               */
            ]}
            actions={actions}
          />

          <DataTable<ForwardedMessage>
            rows={filtered}
            columns={columns}
            rowKey={(m) => m.id}
            selected={mayEdit ? chosen : undefined}
            onSelect={mayEdit ? setChosen : undefined}
            defaultSort={{ key: 'arrived', direction: 'desc' }}
            caption={`${filtered.length} ${filtered.length === 1 ? 'message' : 'messages'}`}
            empty={
              <p className="py-6 text-center text-sm text-muted-foreground">
                Nothing matches those filters.
              </p>
            }
          />
        </>
      )}

      {reading && <ReadDialog message={reading} onClose={() => setReading(null)} />}

      {assigning && (
        <AssignDialog
          message={assigning}
          meters={meters}
          onDone={refresh}
          onClose={() => setAssigning(null)}
        />
      )}

      {editing && (
        <CorrectDialog message={editing} onDone={refresh} onClose={() => setEditing(null)} />
      )}

      {sending && (
        <SendDialog
          message={sending}
          onDone={refresh}
          onClose={() => setSending(null)}
          onError={setError}
        />
      )}
    </section>
  );
}

/** What the row is waiting for, in one word and a colour. */
function StateChip({ message }: { message: ForwardedMessage }) {
  const look =
    message.state === 'sent'
      ? 'border-secondary-300 bg-secondary-50 text-secondary-900'
      : message.state === 'ready'
        ? 'border-primary-300 bg-primary-50 text-primary-900'
        : message.state === 'trash'
          ? 'border-border bg-muted text-muted-foreground'
          : 'border-accent-300 bg-accent-50 text-accent-900';

  const label =
    message.state === 'sent'
      ? 'Sent'
      : message.state === 'ready'
        ? 'Ready to send'
        : message.state === 'trash'
          ? 'In the trash'
          : 'Needs a landlord';

  return (
    <span className={`inline-block rounded-full border px-2 py-0.5 text-xs ${look}`}>
      {label}
      {message.sentAt && (
        <span className="ml-1 opacity-70">{new Date(message.sentAt).toLocaleDateString('en-KE')}</span>
      )}
    </span>
  );
}

/**
 * The message exactly as the handset received it.
 *
 * Unparsed and unedited, because when the parsed fields look wrong this is the
 * only way to find out which of them is — and it is the record of what
 * actually arrived, which is why nothing on this screen can change it.
 */
export function ReadDialog({ message, onClose }: { message: ForwardedMessage; onClose: () => void }) {
  return (
    <Modal
      title="The message as it arrived"
      onClose={onClose}
      footer={
        <button type="button" className="btn-quiet" onClick={onClose}>
          Close
        </button>
      }
    >
      <div className="space-y-3">
        <p className="text-xs text-muted-foreground">
          {new Date(message.receivedAt).toLocaleString('en-KE')}
          {message.sender ? ` · from ${message.sender}` : ''}
        </p>
        <pre className="whitespace-pre-wrap rounded-xl border border-border bg-muted p-3 font-mono text-sm">
          {message.body}
        </pre>
        {message.sentAt && (
          <p className="text-sm text-muted-foreground">
            Sent to {message.sentTo} on {new Date(message.sentAt).toLocaleString('en-KE')}
            {message.sentByLabel ? ` by ${message.sentByLabel}` : ''}.
          </p>
        )}
      </div>
    </Modal>
  );
}

/** Say which landlord a message belongs to. Filing it does not send it. */
export function AssignDialog({
  message,
  meters,
  onDone,
  onClose,
}: {
  message: ForwardedMessage;
  meters: PowerMeter[];
  onDone: () => Promise<void> | void;
  onClose: () => void;
}) {
  const [meterId, setMeterId] = useState(message.meterId ?? '');
  const [error, setError] = useState<string | null>(null);

  const assign = useMutation({
    mutationFn: () => api.assignForwardedMessage(message.id, meterId),
    onSuccess: async () => {
      await onDone();
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Modal
      title="Which landlord is this for?"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-quiet" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={!meterId || assign.isPending}
            onClick={() => assign.mutate()}
          >
            {assign.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            File it here
          </button>
        </>
      }
    >
      <div className="space-y-3">
        {error && (
          <p className="rounded-xl border border-danger-300 bg-danger-50 p-3 text-sm text-danger-700">
            {error}
          </p>
        )}

        <p className="text-sm text-muted-foreground">
          {message.meterNumber
            ? `The message names meter ${message.meterNumber}, which is not on the list.`
            : 'No meter number could be read out of the message.'}{' '}
          Choose whose it is. Nothing is sent until you press Send.
        </p>

        <label className="block text-sm">
          Meter
          <select className="input mt-1 w-full" value={meterId} onChange={(e) => setMeterId(e.target.value)}>
            <option value="">Choose a meter&hellip;</option>
            {meters.map((meter) => (
              <option key={meter.id} value={meter.id}>
                {meter.label} — {meter.landlordName} ({meter.meterNumberFormatted})
              </option>
            ))}
          </select>
          <span className="mt-1 block text-xs text-muted-foreground">
            If the meter genuinely is not on the list, add it above first &mdash; then the next
            token for it matches on its own.
          </span>
        </label>
      </div>
    </Modal>
  );
}

/**
 * Correcting what a parser read.
 *
 * Every parsed field, because they have all been wrong: one of them once read
 * nine units out of a message that said fourteen and a half. The message body
 * is shown and cannot be edited — it is the evidence the corrections are made
 * against.
 */
export function CorrectDialog({
  message,
  onDone,
  onClose,
}: {
  message: ForwardedMessage;
  onDone: () => Promise<void> | void;
  onClose: () => void;
}) {
  const [form, setForm] = useState({
    token: message.token ?? '',
    meterNumber: (message.meterNumber ?? '').replace(/\s/g, ''),
    units: message.units === null ? '' : String(message.units),
    amountKes: message.amountCents === null ? '' : String(message.amountCents / 100),
    receipt: message.receipt ?? '',
  });
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      /*
       * Only what actually changed is sent. A blank field means "leave it",
       * not "clear it" — clearing a token by tabbing through a form is not a
       * thing somebody should be able to do by accident.
       */
      const body: Record<string, unknown> = {};
      const trimmed = form.token.trim();
      if (trimmed && trimmed !== (message.token ?? '')) body.token = trimmed;
      if (form.meterNumber.trim() && form.meterNumber.trim() !== (message.meterNumber ?? '').replace(/\s/g, ''))
        body.meterNumber = form.meterNumber.trim();
      if (form.units.trim()) body.units = Number(form.units);
      if (form.amountKes.trim()) body.amountCents = Math.round(Number(form.amountKes) * 100);
      if (form.receipt.trim() !== (message.receipt ?? '')) body.receipt = form.receipt.trim();
      return api.updateForwardedMessage(message.id, body);
    },
    onSuccess: async () => {
      await onDone();
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  const set = (key: keyof typeof form) => (event: { target: { value: string } }) =>
    setForm({ ...form, [key]: event.target.value });

  return (
    <Modal
      title="Correct what was read"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-quiet" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            Save
          </button>
        </>
      }
    >
      <div className="space-y-3">
        {error && (
          <p className="rounded-xl border border-danger-300 bg-danger-50 p-3 text-sm text-danger-700">
            {error}
          </p>
        )}

        <details className="rounded-xl border border-border p-3 text-sm">
          <summary className="cursor-pointer">The message it was read from</summary>
          <pre className="mt-2 whitespace-pre-wrap font-mono text-xs">{message.body}</pre>
        </details>

        <label className="block text-sm">
          Token
          <input className="input mt-1 w-full font-mono" value={form.token} onChange={set('token')} />
          <span className="mt-1 block text-xs text-muted-foreground">
            Paste the whole KPLC message if that is easier &mdash; the digits are picked out of it.
          </span>
        </label>

        <label className="block text-sm">
          Meter number
          <input
            className="input mt-1 w-full font-mono"
            value={form.meterNumber}
            onChange={set('meterNumber')}
          />
          <span className="mt-1 block text-xs text-muted-foreground">
            Correcting this to a meter that is on the list files the message under that landlord
            straight away.
          </span>
        </label>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-sm">
            Units
            <input className="input mt-1 w-full" inputMode="decimal" value={form.units} onChange={set('units')} />
          </label>
          <label className="block text-sm">
            Amount (KES)
            <input
              className="input mt-1 w-full"
              inputMode="numeric"
              value={form.amountKes}
              onChange={set('amountKes')}
            />
          </label>
        </div>

        <label className="block text-sm">
          M-Pesa receipt
          <input className="input mt-1 w-full font-mono" value={form.receipt} onChange={set('receipt')} />
        </label>
      </div>
    </Modal>
  );
}

/**
 * The confirmation before it leaves the building.
 *
 * It shows who it is going to and on which number, because those are the two
 * facts that make this irreversible — a token keyed into the wrong meter locks
 * it for the period, and no amount of apologising takes the SMS back.
 */
export function SendDialog({
  message,
  onDone,
  onClose,
  onError,
}: {
  message: ForwardedMessage;
  onDone: () => Promise<void> | void;
  onClose: () => void;
  onError: (message: string) => void;
}) {
  const [sent, setSent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const send = useMutation({
    mutationFn: () => api.sendForwardedMessage(message.id),
    onSuccess: async (result) => {
      setSent(result.message);
      await onDone();
    },
    onError: (e: Error) => {
      setError(e.message);
      onError(e.message);
    },
  });

  return (
    <Modal
      title={sent ? 'Sent' : 'Send this token to the landlord'}
      onClose={onClose}
      footer={
        sent ? (
          <button type="button" className="btn-primary" onClick={onClose}>
            Close
          </button>
        ) : (
          <>
            <button type="button" className="btn-quiet" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={send.isPending}
              onClick={() => send.mutate()}
            >
              {send.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              Send it
            </button>
          </>
        )
      }
    >
      <div className="space-y-3">
        {error && (
          <p className="rounded-xl border border-danger-300 bg-danger-50 p-3 text-sm text-danger-700">
            {error}
          </p>
        )}

        {sent ? (
          <p className="text-sm">{sent}</p>
        ) : (
          <>
            <p className="text-sm">
              To <strong>{message.landlordName}</strong> on{' '}
              <strong className="font-mono">{message.landlordPhone}</strong>, for{' '}
              {message.meterLabel}.
            </p>
            <p className="text-sm text-muted-foreground">
              This can only happen once. Sending a landlord a second token makes them believe the
              first one failed, and keying in the old digits locks the meter.
            </p>
            <pre className="whitespace-pre-wrap rounded-xl border border-border bg-muted p-3 font-mono text-xs">
              {message.body}
            </pre>
          </>
        )}
      </div>
    </Modal>
  );
}
